import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { VideoButton, styles } from 'yankees-react-utils';
import StrikeZone from '../../../../components/StrikeZone';
import { usePitchVideoFn } from '../../../../data';

const Container = styled.div`
    margin-bottom: 20px;
    > * {
        margin-bottom: 20px;
    }
`;
const StyledSzContainer = styled.div`
    margin: 20px;
`;
const Header = styled.h4`
    && {
        font-size:18px;
        font-weight: bold;
        text-align: center;
    }`;
const StyledVideoButton = styled(VideoButton)`
    > span {
        font-size: 12px !important;
    }
`;

const NA = (translate) => <div style={{ padding: '150px 0', fontSize: '200%', fontStyle: 'italic', textAlign: 'center' }}>{translate('common.abbr-na')}</div>;

const preparePlayAllCallback = (callback, series, mostRecentLegendCallback) => {
    let mergeResult = [];
    series.forEach((pt) => {
        const matchingLegend = mostRecentLegendCallback.find((x) => x.name == pt.name);
        if (mostRecentLegendCallback.length === 0 || (matchingLegend && matchingLegend.visible)) {
            mergeResult = mergeResult.concat(...pt.data);
        }
    });
    callback(mergeResult);
};

function StrikeZoneChart({ translate, title, series, szContours, isLhb, pitcherView, showHandednessInTitle, isInBats, playAllByCountCallback, ...rest }) {
    const seriesUse = isLhb ? series?.lhb : series?.rhb;
    const contoursUse = isLhb ? szContours?.lhb : szContours?.rhb;
    const pitchVideoFn = usePitchVideoFn();
    const [playAllVis, setPlayAllVis] = useState(true);
    const [mostRecentLegendCallback, setMostRecentLegendCallback] = useState([]);

    const legendClickFn = (data) => {
        const somethingVis = data.filter((x) => x.visible).length >= 1;
        setPlayAllVis(somethingVis);
        setMostRecentLegendCallback(data);
    };

    return (
        <Col md={6}>
            <StyledSzContainer>
                <Header>
                    {title}{showHandednessInTitle ? ` ${translate('baseball.abbr-versus')} ${isLhb ? translate('baseball.lhh') : translate('baseball.rhh')}` : ''}
                    {playAllByCountCallback && playAllVis && <StyledVideoButton icon={translate('common.all')} onClick={() => preparePlayAllCallback(playAllByCountCallback, seriesUse, mostRecentLegendCallback)} color={styles.theme.background.astronaut} size="2.5em" style={{ marginLeft: '7px' }}>{translate('sections.reports.pitcher.play-all')}</StyledVideoButton>}
                    {playAllByCountCallback && !playAllVis && <StyledVideoButton icon={translate('common.all')} color={styles.theme.background.geyser} size="2.5em" style={{ marginLeft: '7px' }}>{translate('sections.reports.pitcher.play-all')}</StyledVideoButton>}
                </Header>
                {seriesUse?.length ? (
                    <StrikeZone
                        enableZoom={false}
                        contourPoints={contoursUse}
                        seriesData={seriesUse}
                        size="lg"
                        height={400}
                        showlegend
                        legendposition="below"
                        yopposite
                        showlhb={isLhb}
                        showoffgrid
                        pitcherView={pitcherView}
                        pitchVideoFn={(!window.$IsDugoutApp && isInBats) ? pitchVideoFn : null}
                        legendClickFn={legendClickFn}
                        {...rest}
                    />
                ) : NA(translate)}
            </StyledSzContainer>
        </Col>
    );
}

const PitchLocationCharts = ({ countseries, strikeZoneContours, lhbOnly = false, rhbOnly = false, pitcherView = true, showHandednessInTitle = true, isInBats = true, playAllByCountCallback = null, ...rest }) => {
    const { t: translate } = useTranslation();
    const szSections = [
        { title: translate('sections.reports.pitcher.counts-all'), series: countseries?.all },
        { title: translate('sections.reports.pitcher.first-pitch'), series: countseries?.firstpitch },
        { title: translate('sections.reports.pitcher.counts-early'), series: countseries?.early },
        { title: translate('sections.opponent.player.hitter-ahead-in-counts'), series: countseries?.hitterahead },
        { title: translate('sections.reports.pitcher.counts-2-strike'), series: countseries?.count2k },
        { title: translate('sections.reports.pitcher.counts-3-2'), series: countseries?.count32 },
    ];
    return (
        <Container>
            {!rhbOnly && !lhbOnly && countseries && szSections.map(({ title, series }) => (
                <Row key={title} className={!rhbOnly && !lhbOnly ? 'justify-content-center' : ''}>
                    {!rhbOnly && (
                        <StrikeZoneChart
                            translate={translate}
                            title={title}
                            series={series}
                            szContours={strikeZoneContours}
                            isLhb
                            pitcherView={pitcherView}
                            showHandednessInTitle={showHandednessInTitle}
                            isInBats={isInBats}
                            playAllByCountCallback={playAllByCountCallback}
                            {...rest}
                        />
                    )}
                    {!lhbOnly && (
                        <StrikeZoneChart
                            translate={translate}
                            title={title}
                            series={series}
                            szContours={strikeZoneContours}
                            isLhb={false}
                            pitcherView={pitcherView}
                            showHandednessInTitle={showHandednessInTitle}
                            isInBats={isInBats}
                            playAllByCountCallback={playAllByCountCallback}
                            {...rest}
                        />
                    )}
                </Row>
            ))}
            {(lhbOnly || rhbOnly)
            && (
                <Row>
                    {rhbOnly && countseries && szSections.map(({ title, series }) => (
                        <StrikeZoneChart
                            translate={translate}
                            title={title}
                            series={series}
                            szContours={strikeZoneContours}
                            isLhb={false}
                            key={`${title}_false`}
                            pitcherView={pitcherView}
                            showHandednessInTitle={showHandednessInTitle}
                            isInBats={isInBats}
                            playAllByCountCallback={playAllByCountCallback}
                            {...rest}
                        />
                    ))}
                    {lhbOnly && countseries && szSections.map(({ title, series }) => (
                        <StrikeZoneChart
                            translate={translate}
                            title={title}
                            series={series}
                            szContours={strikeZoneContours}
                            isLhb
                            key={`${title}_true`}
                            pitcherView={pitcherView}
                            showHandednessInTitle={showHandednessInTitle}
                            isInBats={isInBats}
                            playAllByCountCallback={playAllByCountCallback}
                            {...rest}
                        />
                    ))}
                </Row>
            )}
        </Container>
    );
};

PitchLocationCharts.propTypes = {
    countseries: PropTypes.shape({
        all: PropTypes.shape({}).isRequired,
        early: PropTypes.shape({}).isRequired,
        firstpitch: PropTypes.shape({}).isRequired,
        count11: PropTypes.shape({}).isRequired,
        count2k: PropTypes.shape({}).isRequired,
        count32: PropTypes.shape({}).isRequired,
        behind: PropTypes.shape({}).isRequired,
        hitterahead: PropTypes.shape({}).isRequired,
    }).isRequired,
    strikeZoneContours: PropTypes.shape({
        lhb: PropTypes.arrayOf(PropTypes.shape({})),
        rhb: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    lhbOnly: PropTypes.bool,
    rhbOnly: PropTypes.bool,
    pitcherView: PropTypes.bool,
    showHandednessInTitle: PropTypes.bool,
    isInBats: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    playAllByCountCallback: PropTypes.any,
};

PitchLocationCharts.defaultProps = {
    strikeZoneContours: {
        lhb: [],
        rhb: [],
    },
    lhbOnly: false,
    rhbOnly: false,
    pitcherView: true,
    showHandednessInTitle: true,
    isInBats: true,
    playAllByCountCallback: null,
};

export default PitchLocationCharts;
